// Member activity / change log (tenant-scoped).
// Records who changed what on a member, so staff can review recent edits.
import { ref, push, set, get, query, orderByChild, equalTo, limitToLast } from 'firebase/database';
import { db, auth } from './config';

function tenantPath(tenantId, ...segments) {
  return ['tenants', tenantId, ...segments].filter(Boolean).join('/');
}

export const ACTIVITY_TYPES = {
  MEMBER_CREATED: 'member_created',
  DETAILS_UPDATED: 'details_updated',
  MEMBERSHIP_PAUSED: 'membership_paused',
  MEMBERSHIP_RESUMED: 'membership_resumed',
  MEMBER_DELETED: 'member_deleted',
  WEIGHT_RECORDED: 'weight_recorded'
};

export const ACTIVITY_LABELS = {
  [ACTIVITY_TYPES.MEMBER_CREATED]: { icon: '🆕', label: 'Member registered' },
  [ACTIVITY_TYPES.DETAILS_UPDATED]: { icon: '✏️', label: 'Details updated' },
  [ACTIVITY_TYPES.MEMBERSHIP_PAUSED]: { icon: '⏸️', label: 'Membership paused' },
  [ACTIVITY_TYPES.MEMBERSHIP_RESUMED]: { icon: '▶️', label: 'Membership resumed' },
  [ACTIVITY_TYPES.MEMBER_DELETED]: { icon: '🗑️', label: 'Member deleted' },
  [ACTIVITY_TYPES.WEIGHT_RECORDED]: { icon: '⚖️', label: 'Weight recorded' }
};

// Only these fields are tracked, so engine-owned billing fields never show up as "changes"
const TRACKED_FIELDS = {
  fullName: 'Full Name',
  fullAddress: 'Address',
  email: 'Email',
  phone: 'Phone',
  dateOfBirth: 'Date of Birth',
  age: 'Age',
  sex: 'Sex',
  height: 'Height',
  weight: 'Weight',
  fees: 'Fees',
  paymentMethod: 'Payment Method',
  membershipDuration: 'Membership Duration',
  membershipLevel: 'Membership Level',
  registrationDate: 'Registration Date',
  signature: 'Signature',
  termsAccepted: 'Terms Accepted',
  status: 'Status',
  fitnessGoals: 'Fitness Goals'
};

const FITNESS_GOAL_LABELS = {
  fatReduction: 'Fat Reduction',
  energyEndurance: 'Energy & Endurance',
  muscleStrength: 'Muscle Strength',
  timelessYouth: 'Timeless Youth',
  digestiveHealth: 'Digestive Health',
  sculptedPhysique: 'Sculpted Physique',
  challengeMuscles: 'Challenge Muscles',
  sweatFit: 'Sweat It Out & Get Fit',
  allOfAbove: 'All Of The Above'
};

export const getActorLabel = () => auth.currentUser?.email || auth.currentUser?.uid || 'system';

// Turn a stored value into something readable in the log
const formatValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '—';

  if (field === 'fitnessGoals') {
    if (typeof value !== 'object') return String(value);
    const selected = Object.entries(value)
      .filter(([, isSelected]) => isSelected)
      .map(([key]) => FITNESS_GOAL_LABELS[key] || key);
    return selected.length > 0 ? selected.join(', ') : 'None';
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value).trim() || '—';
};

/**
 * Compare a member before and after an edit and return the readable changes.
 * Only whitelisted detail fields are compared.
 */
export const diffMemberDetails = (before = {}, after = {}) => {
  const changes = [];

  Object.entries(TRACKED_FIELDS).forEach(([field, label]) => {
    if (!(field in after)) return;

    const fromValue = formatValue(field, before[field]);
    const toValue = formatValue(field, after[field]);

    if (fromValue !== toValue) {
      changes.push({ field, label, from: fromValue, to: toValue });
    }
  });

  return changes;
};

/**
 * Append an entry to the member activity log.
 * Logging must never break the operation that triggered it, so failures are swallowed.
 */
export const logMemberActivity = async (tenantId, entry) => {
  try {
    if (!tenantId || !entry?.memberId) return { success: false, message: 'Missing tenant or member' };

    const now = new Date();
    const activity = {
      memberId: entry.memberId,
      memberName: entry.memberName || '',
      type: entry.type || ACTIVITY_TYPES.DETAILS_UPDATED,
      summary: entry.summary || '',
      changes: entry.changes || [],
      details: entry.details || null,
      performedBy: entry.performedBy || getActorLabel(),
      performedAt: now.toISOString(),
      timestamp: now.getTime()
    };

    const activityRef = push(ref(db, tenantPath(tenantId, 'activityLog')));
    await set(activityRef, activity);

    return { success: true, activityId: activityRef.key };
  } catch (error) {
    console.error('Error writing member activity log:', error);
    return { success: false, error: error.code || 'unknown', message: error.message };
  }
};

const snapshotToEntries = (snapshot) => {
  const entries = [];
  snapshot.forEach((child) => {
    entries.push({ id: child.key, ...child.val() });
  });
  // Newest first
  return entries.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
};

// Recent changes for one member
export const getMemberActivity = async (tenantId, memberId, maxEntries = 50) => {
  try {
    const activityRef = ref(db, tenantPath(tenantId, 'activityLog'));
    const memberQuery = query(activityRef, orderByChild('memberId'), equalTo(memberId));
    const snapshot = await get(memberQuery);

    if (!snapshot.exists()) {
      return { success: true, activity: [] };
    }

    return { success: true, activity: snapshotToEntries(snapshot).slice(0, maxEntries) };
  } catch (error) {
    console.error('Error loading member activity:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to load member activity'
    };
  }
};

// Recent changes across every member (for the dashboard feed)
export const getRecentActivity = async (tenantId, maxEntries = 25) => {
  try {
    const activityRef = ref(db, tenantPath(tenantId, 'activityLog'));
    const recentQuery = query(activityRef, orderByChild('timestamp'), limitToLast(maxEntries));
    const snapshot = await get(recentQuery);

    if (!snapshot.exists()) {
      return { success: true, activity: [] };
    }

    return { success: true, activity: snapshotToEntries(snapshot) };
  } catch (error) {
    console.error('Error loading recent activity:', error);
    return {
      success: false,
      error: error.code || 'unknown',
      message: error.message || 'Failed to load recent activity'
    };
  }
};
